import styled, { css } from 'styled-components';
import { Checkbox } from 'ui/checkboxV2';
import { ChevronDown } from 'ui/icons';
import { ExpandableCategoryGroup } from 'ui/selects/ExpandableCategoryGroup';
import {
  borderRadius,
  colors,
  duration,
  easing,
  largeShadow,
  slideRightFade,
  slideUpFade,
  spacing,
  subtleShadow,
  zIndex,
} from 'ui/theme';
import { body2, caption, overline } from 'ui/theme/typography/styles';
import { pxToRem } from 'utils/convert';

export const MenuContainer = styled.div`
  display: inline-block;
  position: relative;
  user-select: none;
`;

export const MenuListContainerWrapper = styled.div`
  position: absolute;
  border-radius: ${borderRadius.lg};
  min-width: ${spacing.x10};
  max-width: ${spacing.x16};
  background: ${colors.white};
  ${largeShadow}
  z-index: 2;
  padding: ${({ hasFilter }) =>
    hasFilter ? `0 ${spacing.threeEights} ${spacing.threeEights}` : spacing.threeEights};
  animation: ${({ isChild }) =>
    isChild
      ? css`
          ${slideRightFade} ${duration.short} ${easing.entrance}
        `
      : css`
          ${slideUpFade} ${duration.short} ${easing.entrance}
        `};

  ${({
    isChild,
    horizontalAlignment,
    verticalAlignment,
  }: {
    isChild: boolean;
    horizontalAlignment: string;
    verticalAlignment: string;
    hasFilter: boolean;
  }) => {
    const horizontalStyles = () => {
      if (isChild) {
        return `left: calc(100% + ${pxToRem(4)});`;
      } else if (horizontalAlignment === 'right') {
        return 'right: 0;';
      } else {
        return 'left: 0;';
      }
    };
    const verticalStyles = () => {
      if (isChild) {
        return '';
      } else if (verticalAlignment === 'up') {
        return `bottom: ${spacing.x3};`;
      } else if (verticalAlignment === 'down') {
        return `top: ${spacing.x3};`;
      } else {
        return '';
      }
    };

    return horizontalStyles() + verticalStyles();
  }}
`;

export const MenuListContainer = styled.ul`
  margin: 0;
  padding: 0;
  max-height: ${spacing.x40};
  overflow-y: auto;
  overflow-x: hidden;

  ::-webkit-scrollbar {
    width: ${spacing.half};
  }

  ::-webkit-scrollbar-track {
    background: ${colors.grey5};
    margin-top: ${spacing.quarter};
    border-radius: ${spacing.x24};
  }

  ::-webkit-scrollbar-thumb {
    background: ${colors.grey25};
  }

  ::-webkit-scrollbar-thumb:hover {
    background: ${colors.grey50};
  }

  [data-test="menu-item"] {
    min-width: 100%;
  }
`;

export const ExpandableMenuItem = styled(ExpandableCategoryGroup)`
  [data-test="interactive-checkbox"] {
    min-width: ${spacing.plusQuarter};
    height: ${spacing.plusQuarter};
    margin-right: ${spacing.eighth};
  }
  
  li {
    padding: 0 ${spacing.threeQuarter};
  }
`;

export const MenuItemText = styled.div`
  ${body2}
  cursor: pointer;
  color: ${colors.textPrimary};
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${spacing.half};
  width: 100%;
  overflow-wrap: anywhere;
  ${({ isActive }: { isActive?: boolean }) => isActive && `color: ${colors.purple100};`}
`;

export const MenuItemWithSideMenu = styled.li`
  display: flex;
  width: 100%;
`;

export const MenuItemContentWrapper = styled.div<{
  isActive?: boolean;
  isDisabled?: boolean;
}>`
  border-radius: ${borderRadius.md};
  ${({ isDisabled }) => !isDisabled && `cursor: pointer;`}
  display: flex;
  width: 100%;
  transition: all ${duration.short} ${easing.entrance};

  &:hover {
    ${({ isDisabled }) => !isDisabled && `background: ${colors.grey5};`}
  }
  && {
    ${({ isActive }) => isActive && `background: ${colors.purple10};`}
  }
`;

export const DividerMenuItem = styled.li`
  border-bottom: ${spacing.single} solid ${colors.grey5};
  list-style: none;
  margin: ${spacing.threeEights} 0;
`;

export const StyledCheckbox = styled(Checkbox)`
  flex-shrink: 0;
  margin-left: ${spacing.threeEights};
  align-items: center;
  gap: 0;
  width: 100%;
`;

export const MenuInputTextWrapper = styled.div`
  padding: ${spacing.threeEights} 0;
  background: ${colors.white};
  border-radius: ${borderRadius.lg} ${borderRadius.lg} 0 0;
  position: sticky;
  z-index: ${zIndex.stickyInput};
  top: 0;
`;

export const MenuItemTextWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.half};
`;

export const MenuItemWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.half};
`;

export const MenuTitleTextWrapper = styled.div`
  ${overline};
  color: ${colors.textSecondary};
  padding: ${spacing.base} ${spacing.threeEights};
`;

export const CountBadge = styled.span`
  ${caption}
  color: ${colors.white};
  background: ${colors.backgroundInteractiveStrong};
  padding: ${spacing.single} ${spacing.quarter};
  border-radius: ${borderRadius.full};
  min-width: ${spacing.plusQuarter};
  text-align: center;
`;

export const ChevronRightWrapper = styled.span`
  width: ${spacing.base};
  height: ${spacing.base};
`;

export const InputMenuItemWrapper = styled.div`
  background: ${colors.white};
  flex: 1 1 auto;
`;

export const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: ${spacing.base};
  > * {
    margin-left: ${spacing.half};
  }
`;

export const StyledIconWrapper = styled.div`
  margin-left: ${spacing.half};
  display: flex;
`;

export const ChevronUp = styled(ChevronDown)`
  transform: rotate(180deg);
`;

export const TriggerButton = styled.button`
  ${body2}
  height: ${pxToRem(42)};
  width: 100%;
  align-items: center;
  color: ${colors.textPrimary};
  display: flex;
  gap: ${spacing.half};
  flex: 1 1 auto;
  border: none;
  justify-content: space-between;
  outline: none;
  ${subtleShadow}
  transition: all ${duration.short} ${easing.entrance};

  svg:not(.shape) path {
    stroke: ${colors.iconPrimary};
    transition: all ${duration.short} ${easing.entrance};
  }
  svg.shape {
    fill: ${colors.iconPrimary};
    transition: all ${duration.short} ${easing.entrance};
  }
  svg[data-temporary-fix] path {
    fill: ${colors.iconPrimary};
    stroke: none;
    transition: all ${duration.short} ${easing.entrance};
  }

  && {
    border: ${spacing.single} solid ${colors.borderPrimary};
  }
  :focus {
    border-color: ${colors.borderInteractive};
    box-shadow: 0 0 0 ${spacing.single} ${colors.borderInteractive};

    svg:not(.shape) path {
      stroke: ${colors.iconInteractive};
    }
    svg.shape {
      color: ${colors.iconInteractive};
    }
    svg[data-temporary-fix] path {
      fill: ${colors.iconInteractive};
      stroke: none;
    }
  }
  :hover {
    border-color: ${colors.purple20};
    background: ${colors.purple10};
    color: ${colors.purple100};

    svg:not(.shape) path {
      stroke: ${colors.iconInteractive};
    }
    svg.shape {
      color: ${colors.iconInteractive};
    }
    svg[data-temporary-fix] path {
      fill: ${colors.iconInteractive};
      stroke: none;
    }
  }
  padding: 0 ${spacing.threeQuarter};
  border-radius: ${spacing.quarter};
  background: ${colors.backgroundPrimary};
`;
